import { useMemo } from "react";
import { useSelector } from "react-redux";

/**
 * Hook that returns the current user's role and permissions in the selected tournament.
 *
 * Roles: 'owner' | 'admin' | 'referee' | null
 *
 * Permission flags:
 *  - canManageParticipants: add/remove teams, refs, admins
 *  - canEditFormat: create/edit phases, brackets, groups, matches
 *  - canManageFixtures: generate, create, edit, delete fixtures
 *  - canAddResults: add/edit match results
 *  - canEditSettings: edit tournament settings
 *  - canGoLive: publish tournament
 *  - canManagePayments: create/edit payments
 *  - canDeleteTournament: delete the tournament
 */
const useTournamentRole = () => {
  const user = useSelector((state) => state.user);
  const { selectedTournament } = useSelector((state) => state.tournament);

  return useMemo(() => {
    const currentUserId = user?.user?.id;
    if (!selectedTournament || !currentUserId) {
      return { role: null, permissions: {} };
    }

    // Determine role
    const ownerId =
      selectedTournament.ownerId?.id ||
      selectedTournament.ownerId?._id ||
      selectedTournament.ownerId;

    let role = null;
    if (ownerId?.toString() === currentUserId.toString()) {
      role = "owner";
    } else {
      const member = selectedTournament.members?.find((m) => {
        const memberId = m.userId?.id || m.userId?._id || m.userId;
        return memberId?.toString() === currentUserId.toString();
      });
      if (member?.role === "tournamentAdmin") role = "admin";
      else if (member?.role === "referee") role = "referee";
    }

    const isOwner = role === "owner";
    const isAdmin = role === "admin";
    const isAdminOrAbove = isOwner || isAdmin;

    return {
      role,
      permissions: {
        canManageParticipants: isAdminOrAbove,
        canEditFormat: isAdminOrAbove,
        canManageFixtures: isAdminOrAbove,
        canAddResults: !!role,
        canEditSettings: isAdminOrAbove,
        canGoLive: isAdminOrAbove,
        canManagePayments: isAdminOrAbove,
        canDeleteTournament: isOwner,
      },
    };
  }, [user, selectedTournament]);
};

export default useTournamentRole;
